/**
 * MovementTab — chronological transfer/assignee history for a registration.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React from 'react';
import { User, ArrowRightLeft, Lock, Users, GitBranch } from 'lucide-react';
import { SummaryCell } from './ui';

// iter-230 (Feb 2026) — Movement tab. Mirrors AssignedTaskDetailPage.MovementTab.
export function MovementTab({ reg, t, isAr }) {
  const events = [];
  const created = reg.created_at || null;
  const initialAssignee = reg.assignee_name || reg.assignee_email;
  if (initialAssignee) {
    events.push({
      type:     'assigned',
      at:       created,
      by:       reg.created_by || 'admin',
      to_name:  initialAssignee,
      to_email: reg.assignee_email,
    });
  }
  // Every stage that carries an assignee_id counts as a transfer event.
  (reg.stages || []).forEach((s) => {
    if (s.assignee_id || s.assignee_name || s.assignee_email) {
      events.push({
        type:        'transferred',
        at:          s.date,
        by_email:    s.actor,
        to_name:     s.assignee_name,
        to_email:    s.assignee_email,
        description: s.note,
      });
    }
  });
  if (reg.closed_at) {
    events.push({
      type: 'closed',
      at:   reg.closed_at,
      by:   reg.updated_by || 'admin',
    });
  }

  const currentAssignee = reg.assignee_name || reg.assignee_email || '—';
  const finalCloser     = reg.closed_at ? (reg.updated_by || 'admin') : null;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(isAr ? 'ar-EG' : 'en-GB') : '');

  return (
    <div className="space-y-4">
      {/* Summary card — three tiles: current holder, transfers, final closer */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryCell icon={User}           colour="#3b82f6"
                       label={t('الحائز الحالي', 'Current holder')} value={currentAssignee} />
          <SummaryCell icon={ArrowRightLeft} colour="#8b5cf6"
                       label={t('عدد التحويلات', 'Transfers')}
                       value={events.filter(e => e.type === 'transferred').length} />
          <SummaryCell icon={Lock}           colour={finalCloser ? '#10b981' : '#6b7280'}
                       label={t('الإغلاق النهائي', 'Final closer')}
                       value={finalCloser || t('لم يُغلَق بعد', 'Not closed yet')} />
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <GitBranch className="w-4 h-4 text-white/50" />
          <h2 className="text-white font-bold text-sm">
            {t('مسار حركة الطلب بين الموظفين', 'Request movement path between staff')}
          </h2>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-8 text-white/40 text-sm">
            {t('لا حركة حتى الآن.', 'No movement yet.')}
          </div>
        ) : (
          <ol className="relative ps-6">
            <span className="absolute inset-y-0 start-2 w-px bg-white/10" aria-hidden />
            {events.map((ev, idx) => {
              let Icon = ArrowRightLeft;
              let iconColour = '#8b5cf6';
              let label = t('تحويل', 'Transferred');
              if (ev.type === 'assigned') { Icon = Users; iconColour = '#3b82f6'; label = t('إسناد أولي', 'Initial assignment'); }
              if (ev.type === 'closed')   { Icon = Lock;  iconColour = '#10b981'; label = t('إغلاق نهائي', 'Final closure'); }
              return (
                <li key={idx} className="relative pb-5 last:pb-0" data-testid={`reg-movement-event-${idx}`}>
                  <span className="absolute -start-6 top-0 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${iconColour}25`, border: `1.5px solid ${iconColour}` }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: iconColour }} />
                  </span>

                  <div className="bg-white/[0.02] border border-white/10 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Icon className="w-3.5 h-3.5" style={{ color: iconColour }} />
                      <span className="text-xs font-bold" style={{ color: iconColour }}>{label}</span>
                      <span className="text-[11px] text-white/40 ms-auto">{fmt(ev.at)}</span>
                    </div>
                    {ev.type === 'assigned' && (
                      <div className="text-sm text-white/80">
                        {t('أُسندت المهمة إلى', 'Task assigned to')}{' '}
                        <span className="font-bold text-white">{ev.to_name || ev.to_email}</span>
                        {ev.by && <span className="text-xs text-white/40 ms-2">({t('بواسطة', 'by')} {ev.by})</span>}
                      </div>
                    )}
                    {ev.type === 'transferred' && (
                      <>
                        <div className="text-sm text-white/80">
                          <span className="font-medium">{ev.by_email || 'admin'}</span>
                          {' → '}
                          <span className="font-bold text-white">{ev.to_name || ev.to_email}</span>
                        </div>
                        {ev.description && (
                          <div className="text-xs text-white/50 mt-1 italic">"{ev.description}"</div>
                        )}
                      </>
                    )}
                    {ev.type === 'closed' && (
                      <div className="text-sm text-white/80">
                        {t('أُغلق الطلب نهائياً بواسطة', 'Request finally closed by')}{' '}
                        <span className="font-bold text-white">{ev.by}</span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
